import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSetUser } from "../contexts/UserContext";
import { useSnackbar } from "../contexts/SnackbarContext";
import { MODULE_PRICING } from "../constants/moduleSubscriptionCatalog";
import {
  passwordMeetsPolicy,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_HELPER_TEXT,
} from "../utils/passwordPolicy";
import { markWelcomeTourExpectedAfterLogin } from "../utils/firstLoginOnboardingStorage";
import PublicMarketingShell from "../components/PublicMarketingShell";
import { publicCardSx, publicTitleSx } from "../constants/publicMarketingTheme";

/** Matches fixed `TopBar` + `App` content offset (`mt: 64px`). */
const TOPBAR_OFFSET_PX = 64;

/** Shown in order while `/api/auth/register` is in flight; stays on the last line (no loop). */
const REGISTRATION_STATUS_MESSAGES = [
  "Connecting to registration service…",
  "Reserving your organization record…",
  "Preparing your dedicated workspace…",
  "Creating database…",
  "Provisioning storage and security settings…",
  "Running schema migrations…",
  "Initializing tables and indexes…",
  "Seeding default configuration…",
  "Creating your administrator account…",
  "Securing credentials…",
  "Applying roles and permissions…",
  "Setting up subscription and module access…",
  "Syncing organization directory…",
  "Warming up your session…",
  "Finalizing setup…",
];

const REGISTRATION_STATUS_INTERVAL_MS = 1000;

const SignUpPage = () => {
  const [step, setStep] = useState("mode");
  const [registrationType, setRegistrationType] = useState(null);
  const [subscriptionCode, setSubscriptionCode] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [orgPhone, setOrgPhone] = useState("");
  const [adminUsername, setAdminUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validatingSignupCode, setValidatingSignupCode] = useState(false);
  const [registrationStatusIndex, setRegistrationStatusIndex] = useState(0);
  const registrationStatusTimerRef = useRef(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const setUser = useSetUser();
  const { showSnackbar } = useSnackbar();

  const trialModuleLabels = MODULE_PRICING.filter((m) => m.available !== false)
    .map((m) => m.label)
    .join(", ");

  useEffect(() => {
    if (!submitting) {
      if (registrationStatusTimerRef.current) {
        clearInterval(registrationStatusTimerRef.current);
        registrationStatusTimerRef.current = null;
      }
      setRegistrationStatusIndex(0);
      return undefined;
    }
    setRegistrationStatusIndex(0);
    registrationStatusTimerRef.current = setInterval(() => {
      setRegistrationStatusIndex((i) => {
        const lastIdx = REGISTRATION_STATUS_MESSAGES.length - 1;
        if (i >= lastIdx) {
          if (registrationStatusTimerRef.current) {
            clearInterval(registrationStatusTimerRef.current);
            registrationStatusTimerRef.current = null;
          }
          return i;
        }
        const next = i + 1;
        if (next >= lastIdx && registrationStatusTimerRef.current) {
          clearInterval(registrationStatusTimerRef.current);
          registrationStatusTimerRef.current = null;
        }
        return next;
      });
    }, REGISTRATION_STATUS_INTERVAL_MS);
    return () => {
      if (registrationStatusTimerRef.current) {
        clearInterval(registrationStatusTimerRef.current);
        registrationStatusTimerRef.current = null;
      }
    };
  }, [submitting]);

  const handleContinueFromCodeStep = async () => {
    if (!registrationType || validatingSignupCode) return;
    const code = subscriptionCode.trim();
    if (!code) return;
    setError("");
    setValidatingSignupCode(true);
    try {
      await axios.post(
        "/api/auth/validate-signup-code",
        { code, registrationType },
        { withCredentials: true },
      );
      setStep("registration");
    } catch (err) {
      const msg = err.response?.data?.message || "Could not verify code";
      setError(msg);
      showSnackbar({ message: msg, severity: "error", duration: 8000 });
    } finally {
      setValidatingSignupCode(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (!passwordMeetsPolicy(adminPassword)) {
      setError(PASSWORD_POLICY_MESSAGE);
      showSnackbar({
        message: PASSWORD_POLICY_MESSAGE,
        severity: "warning",
        duration: 10000,
      });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        orgName,
        orgEmail,
        /** Used as tenant admin user email when no separate `email` field is collected. */
        email: orgEmail.trim() || undefined,
        orgAddress,
        orgPhone,
        adminUsername,
        adminPassword,
        registrationType,
      };
      if (registrationType === "code" || registrationType === "trial") {
        payload.subscriptionCode = subscriptionCode.trim();
      }
      const registrationRes = await axios.post(
        "/api/auth/register",
        payload,
        { withCredentials: true },
      );
      localStorage.setItem("accessToken", "cookie");
      setUser(registrationRes.data.user);
      markWelcomeTourExpectedAfterLogin();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err.response?.data?.message || "Sign up failed";
      setError(msg);
      showSnackbar({ message: msg, severity: "error", duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  const titleForStep =
    step === "mode"
      ? "Get started"
      : step === "code"
        ? "Enter subscription code"
        : step === "trial-code"
          ? "Enter trial code"
          : "Register organization";

  return (
    <PublicMarketingShell
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        maxWidth: "100%",
        minHeight: `calc(100vh - ${TOPBAR_OFFSET_PX}px)`,
        py: 2.5,
        px: { xs: 1.5, sm: 2 },
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          ...publicCardSx,
          p: { xs: 2.5, sm: 4 },
          minWidth: 0,
          width: "100%",
          maxWidth: "min(100%, 640px)",
          height: "auto",
          boxSizing: "border-box",
          mt: { xs: -1, sm: -30 },
          borderRadius: 4,
          boxShadow: "0 18px 42px rgba(0,0,0,0.45)",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={publicTitleSx}
        >
          {titleForStep}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ color: "rgba(255,255,255,0.8)", mb: 2 }}
        >
          {step === "mode"
            ? "Start a trial with a code from your account manager, or register with a prepaid subscription code."
            : step === "code"
              ? "Your organization admin will provide this code after purchase."
              : step === "trial-code"
                ? "Enter the trial invitation code you were given. It unlocks signup and sets your trial length and modules."
                : "Create your organization and administrator account."}
        </Typography>
        {error ? (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
        ) : null}

        {step === "mode" ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              size="large"
              sx={{ py: 1.5, fontWeight: 700, borderRadius: 2 }}
              onClick={() => {
                setRegistrationType("trial");
                setSubscriptionCode("");
                setStep("trial-code");
              }}
            >
              Free trial (trial code required)
            </Button>
            <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.85)" }}>
              Next: enter your trial code. Typical modules: {trialModuleLabels}
            </Typography>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              sx={{ py: 1.5, fontWeight: 700, borderRadius: 2 }}
              onClick={() => {
                setRegistrationType("code");
                setSubscriptionCode("");
                setStep("code");
              }}
            >
              I have a subscription code
            </Button>
          </Stack>
        ) : null}

        {step === "code" || step === "trial-code" ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              required
              fullWidth
              label={step === "trial-code" ? "Trial code" : "Subscription code"}
              value={subscriptionCode}
              onChange={(e) => setSubscriptionCode(e.target.value)}
              color="secondary"
              autoComplete="off"
              inputProps={{
                style: { textTransform: "uppercase" },
              }}
              sx={{ input: { color: "#f9fafb" }, label: { color: "#cbd5e1" } }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                disabled={validatingSignupCode}
                onClick={() => {
                  setError("");
                  setStep("mode");
                  setRegistrationType(null);
                  setSubscriptionCode("");
                }}
              >
                Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                disabled={!subscriptionCode.trim() || validatingSignupCode}
                onClick={handleContinueFromCodeStep}
              >
                {validatingSignupCode ? "Checking…" : "Continue"}
              </Button>
            </Stack>
          </Stack>
        ) : null}

        {step === "registration" ? (
          <form onSubmit={handleSignUp} autoComplete="off">
            <Typography variant="body2" sx={{ mt: 1, mb: 1, color: "rgba(226,232,240,0.9)" }}>
              {registrationType === "trial"
                ? `Trial code: ${subscriptionCode.trim() || "—"} · trial length and modules follow the code.`
                : `Prepaid code: ${subscriptionCode.trim() || "—"}`}
            </Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Organization Name"
              autoComplete="off"
              autoFocus
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              color="secondary"
              disabled={submitting}
              sx={{ input: { color: "#f9fafb" }, label: { color: "#cbd5e1" } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Organization Email"
              autoComplete="off"
              value={orgEmail}
              onChange={(e) => setOrgEmail(e.target.value)}
              color="secondary"
              disabled={submitting}
              sx={{ input: { color: "#f9fafb" }, label: { color: "#cbd5e1" } }}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Organization Address"
              autoComplete="off"
              value={orgAddress}
              onChange={(e) => setOrgAddress(e.target.value)}
              color="secondary"
              disabled={submitting}
              sx={{ input: { color: "#f9fafb" }, label: { color: "#cbd5e1" } }}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Organization Phone"
              autoComplete="off"
              value={orgPhone}
              onChange={(e) => setOrgPhone(e.target.value)}
              color="secondary"
              disabled={submitting}
              sx={{ input: { color: "#f9fafb" }, label: { color: "#cbd5e1" } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Admin Username"
              autoComplete="off"
              value={adminUsername}
              onChange={(e) => setAdminUsername(e.target.value)}
              color="secondary"
              disabled={submitting}
              sx={{ input: { color: "#f9fafb" }, label: { color: "#cbd5e1" } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Admin Password"
              type="password"
              autoComplete="new-password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              color="secondary"
              disabled={submitting}
              helperText={PASSWORD_POLICY_HELPER_TEXT}
              FormHelperTextProps={{ sx: { color: "rgba(148,163,184,0.95)" } }}
              sx={{ input: { color: "#f9fafb" }, label: { color: "#cbd5e1" } }}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setError("");
                  if (registrationType === "code") {
                    setStep("code");
                  } else if (registrationType === "trial") {
                    setStep("trial-code");
                  } else {
                    setStep("mode");
                    setRegistrationType(null);
                  }
                }}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="secondary"
                disabled={submitting}
              >
                {submitting ? "Registering…" : "Register Organization"}
              </Button>
            </Stack>
            {submitting ? (
              <Box sx={{ mt: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={22} sx={{ color: "#4cceac", flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(241,245,249,0.95)", fontWeight: 600 }}
                    >
                      {REGISTRATION_STATUS_MESSAGES[registrationStatusIndex]}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "rgba(148,163,184,0.95)" }}
                    >
                      This can take a minute. Please keep this page open and do not refresh.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : null}
          </form>
        ) : null}
      </Paper>
    </PublicMarketingShell>
  );
};

export default SignUpPage;
